import { useEffect, useRef } from 'react'

/**
 * Trap tab focus inside the returned ref's element while `active` is true.
 * Tabbing past the last focusable element wraps to the first; shift+tab from
 * first wraps to last. Disabled elements are skipped.
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(active: boolean) {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (!active || !ref.current) return
    const el = ref.current
    const focusables = () => Array.from(
      el.querySelectorAll<HTMLElement>(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((f) => !f.hasAttribute('disabled'))

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    el.addEventListener('keydown', handler)
    return () => el.removeEventListener('keydown', handler)
  }, [active])
  return ref
}
